/**
 * RetoursPage — the prescriber-return tracking list. Pick a médecin (from the analytics roster) → load
 * GET /referral/retours?medecin_id= (the delivery-tracking rows: demande, compte rendu + version, validé
 * params, critique flag, canal, statut en_attente|envoye|echec). KPI tiles + a sortable table.
 *
 * Two queries: the roster (analytics — gates the médecin picker + the module-mounted probe) and the
 * selected médecin's returns. Full 5-state DoD + the CLOUD-only "indisponible" state via QueryBoundary
 * (driven by the roster, the screen's primary read). The returns sub-query renders its own inline states.
 */
import { useMemo, useState } from "react";
import { Badge, Card, EmptyState, ErrorState, FormField, LoadingState, Select, Table, type Column } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { REF_CAPS } from "../caps";
import { useAnalytics, useLabId, useRetours } from "../api";
import type { RetourPrescripteur } from "../types";
import { idStr } from "../types";
import { medecinOptions } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { RetourBadge } from "../components/StatusBadge";
import { QueryBoundary } from "../components/QueryBoundary";

export function RetoursPage() {
  const { t } = useT();
  const labId = useLabId();
  const canRoster = useCan(REF_CAPS.analyticsRead);
  const canRead = useCan(REF_CAPS.retourRead);

  const roster = useAnalytics(labId, canRoster);
  const cards = useMemo(() => roster.data?.cards ?? [], [roster.data]);

  const [medecinId, setMedecinId] = useState("");
  const retours = useRetours(labId, medecinId || null, canRead);
  const rows = useMemo(() => retours.data ?? [], [retours.data]);

  const kpis = useMemo(() => {
    let envoye = 0;
    let attente = 0;
    for (const r of rows) {
      if (r.statut === "envoye") envoye += 1;
      else if (r.statut === "en_attente") attente += 1;
    }
    return { total: rows.length, envoye, attente };
  }, [rows]);

  const columns: ReadonlyArray<Column<RetourPrescripteur>> = [
    {
      key: "demande",
      header: t("referral.retours.col.demande"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{idStr(r.demande_analyse_id)}</span>,
    },
    {
      key: "cr",
      header: t("referral.retours.col.cr"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.compte_rendu_numero ?? "—"}</span>,
    },
    {
      key: "version",
      header: t("referral.retours.col.version"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.compte_rendu_version,
      cell: (r) => <span className="nv-ltr-island">v{r.compte_rendu_version}</span>,
    },
    {
      key: "params",
      header: t("referral.retours.col.params"),
      mono: true,
      align: "end",
      cell: (r) => <span className="nv-ltr-island">{r.valide_parameters_count}</span>,
    },
    {
      key: "critique",
      header: t("referral.retours.col.critique"),
      cell: (r) =>
        r.has_critique_result ? (
          <Badge tone="critical">{t("referral.retours.critique.yes")}</Badge>
        ) : (
          <span style={{ color: "var(--nv-slate)" }}>{t("referral.retours.critique.no")}</span>
        ),
    },
    {
      key: "canal",
      header: t("referral.retours.col.canal"),
      cell: (r) => t(`referral.canal.${r.delivery_channel}`),
    },
    {
      key: "statut",
      header: t("referral.retours.col.statut"),
      cell: (r) => <RetourBadge statut={r.statut} />,
    },
    {
      key: "date",
      header: t("referral.retours.col.date"),
      mono: true,
      cell: (r) => (
        <span className="nv-ltr-island">
          {r.returned_at ? new Date(r.returned_at).toLocaleDateString() : "—"}
        </span>
      ),
    },
  ];

  const header = <PageHeader title={t("referral.retours.title")} subtitle={t("referral.retours.subtitle")} />;

  return (
    <QueryBoundary
      forbidden={!canRoster && !canRead}
      forbiddenCapabilities={[REF_CAPS.retourRead]}
      isLoading={roster.isLoading}
      isError={roster.isError}
      error={roster.error ?? null}
      hasData={cards.length > 0}
      onRetry={() => void roster.refetch()}
      header={header}
    >
      {header}

      <Card>
        <div style={{ maxWidth: 420 }}>
          <FormField label={t("referral.medecin.pick")}>
            {(p) => (
              <Select
                {...p}
                value={medecinId}
                onChange={(e) => setMedecinId(e.target.value)}
                placeholder={t("referral.medecin.pick.placeholder")}
                options={medecinOptions(cards)}
                disabled={!canRead}
              />
            )}
          </FormField>
        </div>
      </Card>

      {!canRead ? (
        // The roster is visible but the returns read is not granted — explain inline (sans-droit, scoped).
        <Card>
          <p style={{ margin: 0, color: "var(--nv-slate)" }}>
            {t("state.forbidden.title")} · <span className="nv-mono">{REF_CAPS.retourRead}</span>
          </p>
        </Card>
      ) : !medecinId ? (
        <EmptyState
          glyph="◷"
          title={t("referral.retours.pick.title")}
          description={t("referral.retours.pick.desc")}
        />
      ) : retours.isLoading ? (
        <LoadingState variant="table" rows={5} />
      ) : retours.isError ? (
        <ErrorState
          message={retours.error?.message}
          code={retours.error?.code}
          onRetry={() => void retours.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t("referral.retours.empty.title")}
          description={t("referral.retours.empty.desc")}
        />
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "var(--nv-s-3)",
            }}
          >
            <Kpi label={t("referral.retours.kpi.total")} value={kpis.total} />
            <Kpi
              label={t("referral.retours.kpi.envoye")}
              value={kpis.envoye}
              tokenColor="var(--nv-success)"
            />
            <Kpi
              label={t("referral.retours.kpi.attente")}
              value={kpis.attente}
              tokenColor="var(--nv-warn)"
            />
          </div>
          <Card padded={false}>
            <div style={{ padding: "var(--nv-s-3)" }}>
              <Table
                columns={columns}
                rows={rows}
                rowKey={(r) => idStr(r.id)}
                caption={t("referral.retours.title")}
                pageSize={15}
              />
            </div>
          </Card>
        </>
      )}
    </QueryBoundary>
  );
}

function Kpi({
  label,
  value,
  tokenColor,
}: {
  label: string;
  value: number;
  tokenColor?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-1)",
        padding: "var(--nv-s-3)",
        borderRadius: "var(--nv-r-md)",
        background: "var(--nv-surface-2)",
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      <span style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)" }}>{label}</span>
      <span
        className="nv-ltr-island"
        style={{ fontSize: "var(--nv-fs-h2)", fontWeight: 600, color: tokenColor ?? "var(--nv-ink)" }}
      >
        {value}
      </span>
    </div>
  );
}
